import React, { useMemo } from 'react'
import T from 'prop-types'
import { styled } from '@mui/material/styles'
import { Box, Paper, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import RangeSlider from 'components/atoms/RangeSlider'
import RangeSelect from 'components/atoms/RangeSelect'
import FilterSelect from 'components/atoms/FilterSelect'

import ButtonLink from './ButtonLink'
const classes = {
  filterContainer: 'filter-container',
  filterTitle: 'filter-title',
}
const Root = styled(Paper)(({ theme }) => ({
  [`& .${classes.filterContainer}`]: {
    paddingBottom: theme.spacing(2),
    [`& .${classes.filterTitle}`]: {
      paddingBottom: theme.spacing(0.75),
    },
  },
}))

export default function CarFiltersColumn({
  filters,
  filtersData,
  filteredCars,
  setKmRange,
  setYearRange,
  setBrands,
  setModels,
  setFuels,
  resetFilters,
}) {
  const { t } = useTranslation()

  const filtered = useMemo(() => !!filteredCars, [filteredCars])

  return (
    <Root sx={{ p: 2 }}>
      <Typography variant="body1" color="textPrimary">
        <strong>{t('common.filters.searchFilters')}</strong>
      </Typography>
      {filtered && (
        <>
          <Typography
            variant="caption"
            color="textPrimary"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('common.filters.filteredCars')}{' '}
            <strong>{filteredCars?.length}</strong>
          </Typography>
          <Box />
        </>
      )}

      <ButtonLink variant="caption" color="textPrimary" onClick={resetFilters}>
        {t('common.filters.reset')}
      </ButtonLink>
      <Box pb={3} />
      <Box className={classes.filterContainer}>
        <Typography
          variant="caption"
          component="p"
          color="textPrimary"
          className={classes.filterTitle}
        >
          <strong>{t('common.filters.brand')}</strong>
        </Typography>
        <FilterSelect
          options={filtersData.brands}
          value={filters.brands}
          handleChange={setBrands}
          multiple
          hasSearchField
          searchLabel={t('common.form.searchCarBrand')}
        />
      </Box>

      <Box className={classes.filterContainer}>
        <Typography
          variant="caption"
          component="p"
          color="textPrimary"
          className={classes.filterTitle}
        >
          <strong>{t('common.filters.model')}</strong>
        </Typography>
        <FilterSelect
          options={filtersData.models}
          value={filters.models}
          handleChange={setModels}
          multiple
          hasSearchField
          searchLabel={t('common.form.searchCarModel')}
        />
      </Box>

      <Box className={classes.filterContainer}>
        <Typography
          variant="caption"
          component="p"
          color="textPrimary"
          className={classes.filterTitle}
        >
          <strong>{t('common.filters.kmRange')}</strong>
        </Typography>
        <RangeSlider
          minBound={filtersData.kmMin}
          maxBound={filtersData.kmMax}
          min={filters.kmMin}
          max={filters.kmMax}
          handleRangeChange={setKmRange}
          step={100}
          minDistance={5000}
        />
      </Box>

      <Box className={classes.filterContainer}>
        <Typography
          variant="caption"
          component="p"
          color="textPrimary"
          className={classes.filterTitle}
        >
          <strong>{t('common.filters.year')}</strong>
        </Typography>
        <RangeSelect
          minBound={filtersData.yearMin}
          maxBound={filtersData.yearMax}
          min={filters.yearMin}
          max={filters.yearMax}
          handleRangeChange={setYearRange}
        />
      </Box>

      <Box className={classes.filterContainer}>
        <Typography
          variant="caption"
          component="p"
          color="textPrimary"
          className={classes.filterTitle}
        >
          <strong>{t('common.filters.fuel')}</strong>
        </Typography>
        <FilterSelect
          options={filtersData.fuels}
          value={filters.fuels}
          handleChange={setFuels}
          multiple
          hasSearchField
          searchLabel={t('common.form.searchCarFuel')}
        />
      </Box>
    </Root>
  )
}

CarFiltersColumn.propTypes = {
  filters: T.object.isRequired,
  filtersData: T.object.isRequired,
  filteredCars: T.array,
  setKmRange: T.func.isRequired,
  setYearRange: T.func.isRequired,
  setBrands: T.func.isRequired,
  setModels: T.func.isRequired,
  setFuels: T.func.isRequired,
}
